import { useState, useCallback } from "react";
import { RolePermissionsEntity } from "../../domain/entities/PermissionEntity";
import { PermissionRepositoryImpl } from "../../data/repositories/PermissionRepositoryImpl";
import {
  GetRolePermissionsUseCase,
  UpdateRolePermissionUseCase,
} from "../../domain/use-cases/permissions";

// Create repository instance (singleton)
const permissionRepository = new PermissionRepositoryImpl();

// Create use cases
const getRolePermissionsUseCase = new GetRolePermissionsUseCase(
  permissionRepository
);
const updateRolePermissionUseCase = new UpdateRolePermissionUseCase(
  permissionRepository
);

export interface UseRolePermissionsReturn {
  rolePermissions: RolePermissionsEntity | null;
  loading: boolean;
  error: Error | null;
  fetchRolePermissions: (roleId: number) => Promise<void>;
  updateRolePermission: (
    id: number,
    data: {
      module_code: string;
      action_code: string;
      is_granted: number;
    }
  ) => Promise<void>;
}

export const useRolePermissions = (): UseRolePermissionsReturn => {
  const [rolePermissions, setRolePermissions] =
    useState<RolePermissionsEntity | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRolePermissions = useCallback(async (roleId: number) => {
    try {
      setLoading(true);
      setError(null);
      const permissions = await getRolePermissionsUseCase.execute(roleId);
      setRolePermissions(permissions);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch role permissions")
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRolePermission = useCallback(
    async (
      id: number,
      data: {
        module_code: string;
        action_code: string;
        is_granted: number;
      }
    ): Promise<void> => {
      try {
        setError(null);
        await updateRolePermissionUseCase.execute(id, data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to update role permission")
        );
        throw err;
      }
    },
    []
  );

  return {
    rolePermissions,
    loading,
    error,
    fetchRolePermissions,
    updateRolePermission,
  };
};
